import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { APIProvider, useAPI } from './contexts/APIContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import RouteSeo from './components/RouteSeo';
import { EntitlementGateProvider } from './contexts/EntitlementGateContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { PostHogProvider } from './contexts/PostHogContext';
import GameOpenTracker from './components/GameOpenTracker';
import { SearchProvider } from './contexts/SearchContext';
import { EpisodeNotificationProvider } from './contexts/EpisodeNotificationContext';
import { useSessionTracking } from './hooks/useAnalytics';
import { useReferralCapture } from './hooks/useReferralCapture';
import { captureAdClicks } from './utils/adAttribution';
import { Toaster } from 'react-hot-toast';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingModal from './components/onboarding/OnboardingModal';
import MakeWelcome, { makeWelcomePending } from './components/onboarding/MakeWelcome';
import ParentalConsentGate from './components/ParentalConsentGate';
import Tutorial from './components/Tutorial';
import SubscriptionModal from './components/SubscriptionModal';
import LandingPage from './pages/LandingPage';
import FunnelAuthPage from './pages/FunnelAuthPage';
import { PENDING_GIFT_KEY } from './constants/gift';
import Toast, { showToast } from './components/Toast';
import CloudReveal from './components/games/CloudTransition';
import LoadingScreen from './components/LoadingScreen';
import BackgroundTaskNotifications from './components/BackgroundTaskNotifications';
import VideoDownloadNotification from './components/VideoDownloadNotification';
import './index.css';
import { isFunnelPath } from './utils/funnelPaths';

// Route-level code splitting. The web2app funnel (FunnelAuthPage) and the
// homepage stay in the main bundle: they are the ad landing pages and must
// paint as soon as the JavaScript arrives. Everything else loads on demand,
// so a parent on a phone no longer downloads the audiobook player, the games
// and the blog's syntax highlighter before seeing the first funnel screen.
const Characters = React.lazy(() => import('./pages/Characters'));
const CancelFlowPreview = React.lazy(() => import('./pages/CancelFlowPreview'));
const CharacterLab = React.lazy(() => import('./pages/CharacterLab'));
const Storyverse = React.lazy(() => import('./pages/Storyverse'));
const Create = React.lazy(() => import('./pages/Create'));
const Library = React.lazy(() => import('./pages/Library'));
const AudiobookCreatePage = React.lazy(() => import('./pages/AudiobookCreatePage'));
const AIVoiceStorybookCreatePage = React.lazy(() => import('./pages/AIVoiceStorybookCreatePage'));
const AudiobookPlayer = React.lazy(() => import('./pages/AudiobookPlayer'));
const CreateCharacter = React.lazy(() => import('./pages/CreateCharacter'));
const CreateStory = React.lazy(() => import('./pages/CreateStory'));
const StoryTemplatePage = React.lazy(() => import('./pages/StoryTemplatePage'));
const StorybookTemplatePage = React.lazy(() => import('./pages/StorybookTemplatePage'));
const TemplateDetailPage = React.lazy(() => import('./pages/TemplateDetailPage'));
const CharacterDetail = React.lazy(() => import('./pages/CharacterDetail'));
const StoryDetail = React.lazy(() => import('./pages/StoryDetail'));
const ShowDetail = React.lazy(() => import('./pages/ShowDetail'));
const StandalonePlayer = React.lazy(() => import('./pages/StandalonePlayer'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const InvitePage = React.lazy(() => import('./pages/InvitePage'));
const Settings = React.lazy(() => import('./pages/Settings'));
const ProfilesPage = React.lazy(() => import('./pages/ProfilesPage'));
const LikedShows = React.lazy(() => import('./pages/Profile/LikedShows'));
const GiftPage = React.lazy(() => import('./pages/GiftPage'));
const RedeemPage = React.lazy(() => import('./pages/RedeemPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const BlogList = React.lazy(() => import('./pages/BlogList'));
const BlogPostRoute = React.lazy(() => import('./pages/BlogPostRoute'));
const ShareCharacter = React.lazy(() => import('./pages/ShareCharacter'));
const ShareEpisode = React.lazy(() => import('./pages/ShareEpisode'));
const TestBackgroundTasks = React.lazy(() => import('./pages/TestBackgroundTasks'));
const ExampleVideo = React.lazy(() => import('./pages/ExampleVideo'));
const DownloadPage = React.lazy(() => import('./pages/DownloadPage'));
const WeeklyChallengePage = React.lazy(() => import('./pages/WeeklyChallengePage'));
const GamesPage = React.lazy(() => import('./pages/GamesPage'));
const LearnPage = React.lazy(() => import('./pages/LearnPage'));
const AlphabetWorldsPage = React.lazy(() => import('./pages/AlphabetWorldsPage'));
const AlphabetWorldMapPage = React.lazy(() => import('./pages/AlphabetWorldMapPage'));
const LearnLayout = React.lazy(() => import('./pages/LearnLayout'));
const CountingWorldsPage = React.lazy(() => import('./pages/CountingWorldsPage'));
const CountingWorldMapPage = React.lazy(() => import('./pages/CountingWorldMapPage'));
const CountingLessonPlayer = React.lazy(() => import('./components/learn/counting/CountingLessonPlayer'));
const LetterCoursePlayer = React.lazy(() => import('./components/learn/letters/LetterCoursePlayer'));
const WritingTownPage = React.lazy(() => import('./pages/WritingTownPage'));
const WritingLessonPlayer = React.lazy(() => import('./components/learn/writing/WritingLessonPlayer'));
const BubblePopPage = React.lazy(() => import('./pages/BubblePopPage'));
const ColorBubblePopPage = React.lazy(() => import('./pages/ColorBubblePopPage'));
const StoryPuzzlePage = React.lazy(() => import('./pages/StoryPuzzlePage'));
const AnimalSoundFarmPage = React.lazy(() => import('./pages/AnimalSoundFarmPage'));
const WordWebDefenderPage = React.lazy(() => import('./pages/WordWebDefenderPage'));
const MathCastleDefensePage = React.lazy(() => import('./pages/MathCastleDefensePage'));
const FeedMonsterPage = React.lazy(() => import('./pages/FeedMonsterPage'));
const CritterCrossingPage = React.lazy(() => import('./pages/CritterCrossingPage'));
const CityHelpersPage = React.lazy(() => import('./pages/CityHelpersPage'));
const DesignSystem = React.lazy(() => import('./pages/DesignSystem'));
const TestLoaders = React.lazy(() => import('./pages/TestLoaders'));

// Redirect non-www → www before React mounts so OAuth PKCE sessionStorage stays
// on the same origin. pixleyai.com and www.pixleyai.com are different origins,
// so sessionStorage written on one is invisible to the other — causing the first
// Google login attempt to fail because the PKCE code verifier is missing.
if (window.location.hostname === 'pixleyai.com') {
  window.location.replace(window.location.href.replace('://pixleyai.com', '://www.pixleyai.com'));
}

// Layout component that handles navigation visibility

// Learn: games take the whole screen; hubs and world maps keep the site nav.
const isLearnGameRoute = (p) =>
  /^\/learn\/letters\/[a-z]\/[^/]+$/i.test(p) ||
  /^\/learn\/counting\/\d+\/[^/]+$/i.test(p) ||
  /^\/learn\/writing\/lesson\//i.test(p);

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { loading, user, subscriptionStatus } = useAuth();
  const { showOnboarding } = useOnboarding();

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  // While onboarding is active, render ONLY the onboarding modal — no navbar and
  // no routes behind it. Previously the routes (e.g. Storyverse) + navbar still
  // rendered under the overlay and bled through, since nothing blocked them.
  if (showOnboarding) {
    return <OnboardingModal />;
  }

  // Families from make.pixleyai.com (a paid occasion video) skip the generic onboarding: the backend seeded a
  // persona with their character. They get a short welcome → first episode → membership instead.
  const makeWelcome = makeWelcomePending(user) || (
    process.env.NODE_ENV === 'development' && localStorage.getItem('pixley_make_welcome_preview') === 'true'
      ? { persona: { child_name: 'Pran' }, make: { status: 'pending', star_name: 'Pran', occasion: 'halloween', loves: ['dinosaurs', 'soccer', 'pancakes'], preview_video: '/assets/make/examples/halloween.mp4', preview_poster: '/assets/make/examples/halloween.jpg' } }
      : null);
  // Every funnel entry is exempt (utils/funnelPaths), so a make.pixleyai.com
  // family who opens a funnel link still gets the funnel, not this welcome.
  if (makeWelcome && !isFunnelPath(location.pathname)) {
    return <MakeWelcome make={makeWelcome.make} persona={makeWelcome.persona} />;
  }

  // Verifiable parental consent: once a member (the card charge IS the COPPA
  // verification step), the app stays blocked until a parent records consent.
  // Runs only while the cutover flag is live; accounts that already consented
  // on iOS carry personalization.parental_consent and skip straight through.
  const needsParentalConsent =
    subscriptionStatus?.subscriptionRequired === true &&
    subscriptionStatus?.entitlementActive === true &&
    !!user?.id &&
    user?.personalization?.parental_consent?.agreed !== true;
  const consentPreview =
    process.env.NODE_ENV === 'development' &&
    localStorage.getItem('pixley_consent_preview') === 'true';
  if (needsParentalConsent || consentPreview) {
    return <ParentalConsentGate />;
  }

  // Don't show navigation on auth page or public pages. Share links DO show the
  // navbar (so viewers can navigate into Pixley / sign up), matching the legacy
  // /shareCharacters and /shareEpisodes routes.
  // Blog (/blog, /blog/:slug) intentionally shows Navigation — same shared
  // toolbar as Landing and Storyverse. BlogList/BlogPost already pad content
  // clear of the fixed navbar (isBlogPage / mergedLight styling in Navigation).
  const hideNavigation = isFunnelPath(location.pathname) ||
                         ['/auth', '/invite', '/privacy-policy', '/example', '/download', '/design-system'].includes(location.pathname) ||
                         location.pathname === '/games/math-castle-defense' ||
                         location.pathname === '/__preview/math-castle-defense' ||
                         isLearnGameRoute(location.pathname) ||
                         location.pathname.startsWith('/sitemap');

  return (
    <>
      {!hideNavigation && <Navigation />}
      <main>
        {children}
      </main>
    </>
  );
};

// Redirect helper that preserves query params (UTMs, gclid, fbclid, etc.)
const RedirectWithParams = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

// One web2app funnel page (FunnelAuthPage) with two entries: the default,
// and the cold-traffic "intro" entry that walks the explainer slides first.
// Both are served from /funnel-auth, which is what lets the intro entry
// exist without touching infrastructure:
//
//   - CloudFront: the edge function's SPA rule matches exact-or-slash, so
//     /funnel-auth/intro already resolves. A path of its own (/funnel-intro)
//     does not, and would need the function republished.
//   - Supabase: the Google round trip returns to bare /funnel-auth, the one
//     redirect URL already known to be allowlisted. The `variant` param picks
//     the entry back up on return; Supabase matches on the URL, not its query.
const FunnelAuthRoute = () => {
  const [searchParams] = useSearchParams();
  return <FunnelAuthPage intro={searchParams.get('variant') === 'intro'} />;
};

// Root route - landing page for guests, Storyverse for signed-in users
const RootRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  // Signed-in users go to Storyverse — preserve UTMs/gclid/fbclid from ad clicks
  if (user) {
    return <Navigate to={`/storyverse${location.search}`} replace />;
  }

  return <LandingPage />;
};

// Session tracking wrapper
const AppContent = () => {
  const { user, loading, refreshSubscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const location = useLocation();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionOffer, setSubscriptionOffer] = useState(null);

  // Win-back email link (?offer=winback): open the plans with the welcome-back
  // discount. Signed-out visitors sign in first and come back here.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    if (params.get('offer') !== 'winback') return;
    if (!user) {
      window.location.assign(`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setSubscriptionOffer('winback');
    setShowSubscriptionModal(true);
  }, [loading, user, location.pathname, location.search]);

  useSessionTracking();
  useReferralCapture(); // CRITICAL: Capture referral ID from URL on app load
  // Ad click ids (gclid / fbclid / UTMs) are kept from the landing URL on
  // every page, not only the funnel's: an ad's sitelink lands on a story or
  // learning page first, and the funnel reads the stored click later
  useEffect(() => {
    captureAdClicks();
  }, []);

  // GIFT SAFETY NET: a redeem link stashes its token before signup. New
  // signups get detoured through onboarding and may never return to
  // /redeem — which used to strand them at the paywall with an unredeemed
  // gift. Redeem here the moment auth is ready, anywhere in the app except
  // the redeem page itself (which owns its own flow).
  const giftRedeemAttempted = useRef(false);
  useEffect(() => {
    if (loading || !user?.id || !apiClient) return;
    if (location.pathname.startsWith("/redeem")) return;
    if (giftRedeemAttempted.current) return;
    let pendingToken = null;
    try { pendingToken = localStorage.getItem(PENDING_GIFT_KEY); } catch {}
    if (!pendingToken) return;
    giftRedeemAttempted.current = true;

    (async () => {
      try {
        const authToken = apiClient.getAuthToken();
        const response = await fetch(`${apiClient.baseURL}/api/gifts/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ token: pendingToken }),
          credentials: "include",
        });
        if (response.ok || response.status === 400) {
          // 400 = already redeemed — either way, this token is spent.
          try { localStorage.removeItem(PENDING_GIFT_KEY); } catch {}
          if (response.ok) {
            showToast("🎁 Your gift is unlocked — Pixley Premium is active!", "success");
            try { localStorage.removeItem("pixley_show_subscription_cta"); } catch {}
            try { await refreshSubscriptionStatus(); } catch {}
          }
        } else if (response.status === 403) {
          showToast("Your gift was sent to a different email — sign in with that address to claim it.", "error");
        }
      } catch {
        giftRedeemAttempted.current = false; // transient failure: retry next mount
      }
    })();
  }, [loading, user?.id, apiClient, location.pathname, refreshSubscriptionStatus]);

  // overflow-x uses clip (not hidden): clip doesn't create a scroll
  // container, so position:sticky keeps working for pinned sections
  return (
    <div
      className="App min-h-screen bg-gradient-to-br from-gray-900 to-gray-900 text-gray-900"
      style={{ overflowX: "clip" }}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          style: { zIndex: 100000 },
        }}
        containerStyle={{
          zIndex: 100000,
          top: 96,
        }}
      />
      <Toast />
      <GameOpenTracker />
      <CloudReveal />
      <AppLayout>
                  <React.Suspense fallback={<LoadingScreen message="Loading..." />}>
                  <Routes>
                  {/* Root route */}
                  <Route path="/" element={<RootRedirect />} />
                  
                  {/* Auth route */}
                  <Route path="/auth" element={<AuthPage />} />
                  {/* Referral landing: public, captures ?ref= then routes into the funnel. */}
                  <Route path="/invite" element={<InvitePage />} />
                  <Route path="/funnel" element={<RedirectWithParams to="/funnel-auth" />} />
                  {/* Serves funnel one, or funnel two on ?variant=intro — which is
                      also where the Google round trip lands. See FunnelAuthRoute. */}
                  <Route path="/funnel-auth" element={<FunnelAuthRoute />} />
                  {/* The intro entry: the same funnel with the explainer first.
                      Both /funnel-auth/intro and /funnel-intro resolve at the
                      edge (checked 2026-10-16). */}
                  <Route path="/funnel-auth/intro" element={<FunnelAuthPage intro />} />
                  {/* Kept so the tidier paths work once the edge function is
                      republished; they 404 at CloudFront until then. */}
                  <Route path="/funnel-2" element={<RedirectWithParams to="/funnel-intro" />} />
                  <Route path="/funnel-intro" element={<FunnelAuthPage intro />} />
                  
                  {/* Public Storyverse - accessible to everyone */}
                  <Route path="/storyverse" element={<Storyverse />} />

                  <Route path="/create" element={
                    <ProtectedRoute>
                      <Create />
                    </ProtectedRoute>
                  } />

                  <Route path="/library" element={
                    <ProtectedRoute>
                      <Library />
                    </ProtectedRoute>
                  } />

                  <Route path="/weekly-challenge" element={
                    <ProtectedRoute>
                      <WeeklyChallengePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games" element={
                    <ProtectedRoute>
                      <GamesPage />
                    </ProtectedRoute>
                  } />

                  {/* Pixley Learn: one parent route owns progress + transitions. */}
                  <Route path="/learn" element={
                    <ProtectedRoute>
                      <LearnLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<LearnPage />} />
                    <Route path="letters" element={<AlphabetWorldsPage />} />
                    <Route path="letters/:worldId" element={<AlphabetWorldMapPage />} />
                    <Route path="letters/:letter/:gameId" element={<LetterCoursePlayer />} />
                    <Route path="counting" element={<CountingWorldsPage />} />
                    <Route path="counting/world/:worldId" element={<CountingWorldMapPage />} />
                    <Route path="counting/:lessonNumber/:activityId" element={<CountingLessonPlayer />} />
                    <Route path="writing" element={<WritingTownPage />} />
                    <Route path="writing/lesson/:townLessonId" element={<WritingLessonPlayer />} />
                    <Route path="writing/lesson/:townLessonId/:activityId" element={<WritingLessonPlayer />} />
                  </Route>

                  {/* Deliberately PUBLIC: beat-my-score challenge links land
                      here, and the recipient usually has no account yet.
                      Guests can play; saving progress and the games hub stay
                      behind auth (the page itself guards those). */}
                  <Route path="/games/bubble-pop" element={<BubblePopPage />} />

                  <Route path="/games/color-bubble-pop" element={
                    <ProtectedRoute>
                      <ColorBubblePopPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/story-puzzle" element={
                    <ProtectedRoute>
                      <StoryPuzzlePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/animal-sound-farm" element={
                    <ProtectedRoute>
                      <AnimalSoundFarmPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/word-web-defender" element={
                    <ProtectedRoute>
                      <WordWebDefenderPage />
                    </ProtectedRoute>
                  } />



                  <Route path="/games/math-castle-defense" element={
                    <ProtectedRoute>
                      <MathCastleDefensePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/feed-monster" element={
                    <ProtectedRoute>
                      <FeedMonsterPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/critter-crossing" element={
                    <ProtectedRoute>
                      <CritterCrossingPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/city-helpers" element={
                    <ProtectedRoute>
                      <CityHelpersPage />
                    </ProtectedRoute>
                  } />

                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/math-castle-defense" element={<MathCastleDefensePage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/feed-monster" element={<FeedMonsterPage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/critter-crossing" element={<CritterCrossingPage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/city-helpers" element={<CityHelpersPage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/cancel-flow" element={<CancelFlowPreview />} />
                  )}


                  <Route path="/audiobooks" element={
                    <ProtectedRoute>
                      <Navigate to="/library" replace />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/create" element={
                    <ProtectedRoute>
                      <AudiobookCreatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/create-ai-voice" element={
                    <ProtectedRoute>
                      <AIVoiceStorybookCreatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/:audiobookId" element={<AudiobookPlayer />} />

                  {/* Storybooks (public-facing URL; same player as audiobooks) */}
                  <Route path="/storybooks/:audiobookId" element={<AudiobookPlayer />} />

                  {/* Legacy routes - keep for backwards compatibility */}
                  <Route path="/character-lab" element={
                    <ProtectedRoute>
                      <CharacterLab />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/characters" element={
                    <ProtectedRoute>
                      <Characters />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/characters/:id" element={
                    <ProtectedRoute>
                      <CharacterDetail />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/create-character" element={
                    <ProtectedRoute>
                      <CreateCharacter />
                    </ProtectedRoute>
                  } />
                  
                  {/* Public story detail - guests can view public stories */}
                  <Route path="/stories/:id" element={<StoryDetail />} />
                  
                  <Route path="/create-story" element={
                    <ProtectedRoute>
                      <CreateStory />
                    </ProtectedRoute>
                  } />

                  <Route path="/story-template" element={
                    <ProtectedRoute>
                      <StoryTemplatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/story-template/:templateId" element={
                    <ProtectedRoute>
                      <TemplateDetailPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/storybook-template" element={
                    <ProtectedRoute>
                      <StorybookTemplatePage />
                    </ProtectedRoute>
                  } />

                  {/* Public Storyverse show detail - guests can view but can't watch episodes */}
                  <Route path="/storyverse/shows/:id" element={<ShowDetail isStoryverse={true} />} />

                  {/* Standalone show — plays first episode directly */}
                  <Route path="/storyverse/standalone/:id" element={<StandalonePlayer />} />

                  
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />

                  <Route path="/profiles" element={
                    <ProtectedRoute>
                      <ProfilesPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/profile/liked" element={
                    <ProtectedRoute>
                      <LikedShows />
                    </ProtectedRoute>
                  } />

                  {/* Public: guests can buy gifts (gift.pixleyai.com) */}
                  <Route path="/gift" element={<GiftPage />} />

                  <Route path="/redeem" element={<RedeemPage />} />

                  <Route path="/test-background-tasks" element={
                    <ProtectedRoute>
                      <TestBackgroundTasks />
                    </ProtectedRoute>
                  } />
                  
                  {/* Design System reference page */}
                  <Route path="/design-system" element={<DesignSystem />} />

                  {/* Temp: loader test page */}
                  <Route path="/test-loaders" element={<TestLoaders />} />

                  {/* Public routes */}
                  {/* Landing page moved to the root; keep old links working */}
                  <Route path="/about" element={<RedirectWithParams to="/" />} />
                  <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/example" element={<ExampleVideo />} />
                  <Route path="/download" element={<DownloadPage />} />

                  {/* Blog routes */}
                  <Route path="/blog" element={<BlogList />} />
                  {/* Legacy markdown posts + Revnu posts (any depth, /blog + canonicalPath) */}
                  <Route path="/blog/*" element={<BlogPostRoute />} />
                  
                  {/* Sitemap routes - dynamically fetch from API */}
                  {/* <Route path="/sitemap" element={<Sitemap />} />
                  <Route path="/sitemap.xml" element={<Sitemap />} />
                  <Route path="/sitemap/:sitemapFile" element={<Sitemap />} /> */}

                  {/* Old share routes (legacy) */}
                  <Route path="/shareCharacters/:id" element={<ShareCharacter />} />
                  <Route path="/shareEpisodes/:id" element={<ShareEpisode />} />

                  {/* New public share routes */}
                  <Route path="/share/character/:id" element={<CharacterDetail isPublic={true} />} />
                  <Route path="/share/episode/:id" element={<StoryDetail isPublic={true} />} />

                  {/* Catch all route - redirect to home */}
                  <Route path="*" element={<RedirectWithParams to="/" />} />
                  </Routes>
                  </React.Suspense>
                </AppLayout>

                {/* Global components (OnboardingModal is rendered by AppLayout
                    as a blocking gate when onboarding is active) */}
                <SubscriptionModal
                  isOpen={showSubscriptionModal}
                  onClose={() => { setShowSubscriptionModal(false); setSubscriptionOffer(null); }}
                  apiClient={apiClient}
                  user={user}
                  offer={subscriptionOffer}
                />
                <Tutorial />
                <BackgroundTaskNotifications />
                <VideoDownloadNotification />
              </div>
  );
};

// moments.pixleyai.com (one-off occasion videos from ads; make.pixleyai.com is its old name, now a Cloudflare
// redirect to it) is its own lazy-loaded app: none of the providers below (auth, onboarding, entitlement gates)
// run there. make. still boots it too, in case the redirect is ever off. moments.localhost / make.localhost work
// for local development.
const MakeApp = React.lazy(() => import('./make/MakeApp'));
export const isMakeHost = () =>
  typeof window !== 'undefined' && /^(make|moments)\./i.test(window.location.hostname);

function App() {
  if (isMakeHost()) {
    return (
      <React.Suspense fallback={null}>
        <MakeApp />
      </React.Suspense>
    );
  }
  return (
    <PostHogProvider>
      <AuthProvider>
        <APIProvider>
          <ProfileProvider>
            <OnboardingProvider>
              <TutorialProvider>
                <SearchProvider>
                  <EpisodeNotificationProvider>
                    <Router>
                      {/* Inside Router: TrialWall navigates on the guest CTA */}
                      <EntitlementGateProvider>
                        <RouteSeo />
                        <AppContent />
                      </EntitlementGateProvider>
                    </Router>
                  </EpisodeNotificationProvider>
                </SearchProvider>
              </TutorialProvider>
            </OnboardingProvider>
          </ProfileProvider>
        </APIProvider>
      </AuthProvider>
    </PostHogProvider>
  );
}

export default App;
